'use client';

import { c as _c } from "react/compiler-runtime";
import { type MarketId, MarketUtils } from '@morpho-org/blue-sdk';
import { getIsPositionUnhealthy, ZERO } from '@morpho-org/dapp-core';
import { HoverableCell } from '@morpho-org/ui-v6/client';
import { cn } from '@morpho-org/ui-v6/utils';
import { ProtectedValueIndicator, useProtectedLltv } from '@/components/common/AutoDeleverage/ProtectedLltv';
import { LoanToValueCellHoverContent } from '@/components/common/Cells/LoanToValueCell';
import type { HealthFactorCellProps } from './HealthFactorCell.types';
import { getAdjustedFallbackHealthMetrics } from './HealthFactorCell.helpers';
const HealthFactorCell = t0 => {
  const $ = _c(42);
  const {
    data,
    chainId
  } = t0;
  const marketId = data.market.marketId as MarketId;
  const t1 = data.health.lltv ?? undefined;
  let t2;
  if ($[0] !== chainId || $[1] !== marketId || $[2] !== t1) {
    t2 = {
      lltv: t1,
      marketId,
      chainId
    };
    $[0] = chainId;
    $[1] = marketId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const protectedLltv = useProtectedLltv(t2);
  let t3;
  if ($[4] !== data.health.healthFactor) {
    t3 = data.health.healthFactor == null ? "N/A" : data.health.healthFactor >= 10 ? "> 10" : data.health.healthFactor.toFixed(3);
    $[4] = data.health.healthFactor;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const formattedHealthFactor = t3;
  let t4;
  if ($[6] !== data.health.liquidationPrice || $[7] !== data.health.lltv || $[8] !== protectedLltv) {
    t4 = getAdjustedFallbackHealthMetrics({
      lltv: data.health.lltv,
      protectedLltv,
      liquidationPrice: data.health.liquidationPrice
    });
    $[6] = data.health.liquidationPrice;
    $[7] = data.health.lltv;
    $[8] = protectedLltv;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const fallbackHealthMetrics = t4;
  let t5;
  if ($[10] !== data.borrowShares || $[11] !== data.collateral.amount || $[12] !== data.market.state || $[13] !== fallbackHealthMetrics || $[14] !== protectedLltv) {
    t5 = (!data.collateral.amount || !data.borrowShares || !data.market.state?.borrowAssets || !data.market.state.borrowShares || !protectedLltv ? fallbackHealthMetrics.liquidationPrice : MarketUtils.getLiquidationPrice({
      collateral: data.collateral.amount,
      borrowShares: data.borrowShares
    }, {
      totalBorrowAssets: data.market.state.borrowAssets,
      totalBorrowShares: data.market.state.borrowShares
    }, {
      lltv: protectedLltv
    })) ?? fallbackHealthMetrics.liquidationPrice ?? null;
    $[10] = data.borrowShares;
    $[11] = data.collateral.amount;
    $[12] = data.market.state;
    $[13] = fallbackHealthMetrics;
    $[14] = protectedLltv;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const liquidationPrice = t5;
  const t6 = data.health.ltv ?? ZERO;
  const t7 = protectedLltv ?? ZERO;
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = getIsPositionUnhealthy(t6, t7);
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const isPositionUnhealthy = t8;
  const t9 = data.health.ltv ?? null;
  const t10 = data.health.decimals ?? 18;
  const t11 = `${data.market.loanAsset.symbol} / ${data.market.collateralAsset?.symbol}`;
  let t12;
  if ($[19] !== chainId || $[20] !== isPositionUnhealthy || $[21] !== liquidationPrice || $[22] !== marketId || $[23] !== protectedLltv || $[24] !== t10 || $[25] !== t11 || $[26] !== t9) {
    t12 = <LoanToValueCellHoverContent isUnhealthy={isPositionUnhealthy} ltv={t9} lltv={protectedLltv} marketId={marketId} chainId={chainId} liquidationPrice={liquidationPrice} decimals={t10} liquidationPriceUnit={t11} withProtectedLltv={true} />;
    $[19] = chainId;
    $[20] = isPositionUnhealthy;
    $[21] = liquidationPrice;
    $[22] = marketId;
    $[23] = protectedLltv;
    $[24] = t10;
    $[25] = t11;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = isPositionUnhealthy ? "text-destructive-foreground" : "text-foreground";
  let t14;
  if ($[28] !== t13) {
    t14 = cn("text-xs", t13);
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== formattedHealthFactor || $[31] !== t14) {
    t15 = <span className={t14}>{formattedHealthFactor}</span>;
    $[30] = formattedHealthFactor;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== chainId || $[34] !== marketId) {
    t16 = <ProtectedValueIndicator marketId={marketId} chainId={chainId} />;
    $[33] = chainId;
    $[34] = marketId;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t15 || $[37] !== t16) {
    t17 = <div className="flex gap-[2px]">{t15}{t16}</div>;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t12 || $[40] !== t17) {
    t18 = <HoverableCell hoverCardContent={t12}>{t17}</HoverableCell>;
    $[39] = t12;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  return t18;
};
export default HealthFactorCell;